// The whole error taxonomy, as one pure function.
//
// Classification keys on STRUCTURED fields first (kind, HTTP status, SSM's
// numeric ErrorCode) and only falls back to matching English message text
// inside validation errors, because SSM's codes are coarse: 1001 Invalid
// Request, 1002 Validation Error, 1003 Exception, 1004 DayEnd in progress.
//
// Unmatched failures fail CLOSED (VALIDATION, never retried). We do not retry
// something we do not understand.

import type { PmsError } from './errors';
import type { PmsOp } from './types';

export interface VendorFailureSignal {
  kind: 'http' | 'application' | 'network';
  httpStatus?: number;
  errorCode?: number;
  message?: string;
  raw?: unknown;
}

/** Day-end runs nightly; a job queued during it must outlive it, not die. */
export const DAY_END_RETRY_MS = 30 * 60 * 1000;
/** The 403 daily transaction limit resets with the vendor's day. */
export const DAILY_LIMIT_RETRY_MS = 6 * 60 * 60 * 1000;

const SCHEMA_MISMATCH = /did not match the expected ssm schema/i;
const AUTH = /does not have access|not authorized|unauthorized|gatewaydetails not valid/i;
const DAY_END = /day\s*end is in progress/i;

/**
 * A failure is ambiguous when the write may or may not have landed. For a
 * payment that means a human has to look, because SSM has no idempotency key
 * and no way for us to ask "did you already record this?" in phase 1.
 */
export function isAmbiguous(sig: VendorFailureSignal, op: PmsOp): boolean {
  if (op !== 'recordPayment') return false;
  if (sig.kind === 'network' && !SCHEMA_MISMATCH.test(sig.message || '')) return true;
  if ((sig.httpStatus || 0) >= 500) return true;
  return false;
}

export function classifySsmFailure(sig: VendorFailureSignal, op: PmsOp): PmsError {
  const message = sig.message || 'SSM reported a failure with no message.';
  const base = {
    kind: sig.kind,
    message,
    httpStatus: sig.httpStatus,
    errorCode: sig.errorCode,
    raw: sig.raw,
  };

  // 1. The client's zod schema disagrees with the live API. Retrying fails
  //    identically — this is a library fix, not a transient blip.
  if (sig.kind === 'network' && SCHEMA_MISMATCH.test(message)) {
    return { ...base, kind: 'ours', class: 'VALIDATION' };
  }

  // 2. Never reached the vendor (DNS, timeout, reset).
  if (sig.kind === 'network') {
    return { ...base, class: op === 'recordPayment' ? 'AMBIGUOUS' : 'RETRYABLE' };
  }

  // 3. Credentials / permissions: every job for this facility will fail.
  if (sig.httpStatus === 401 || AUTH.test(message)) {
    return { ...base, class: 'AUTH' };
  }

  // 4. Daily transaction limit.
  if (sig.httpStatus === 403) {
    return { ...base, class: 'RETRYABLE_WINDOW', retryAfterMs: DAILY_LIMIT_RETRY_MS };
  }

  // 5. Day-end in progress.
  if (sig.errorCode === 1004 || DAY_END.test(message)) {
    return { ...base, class: 'RETRYABLE_WINDOW', retryAfterMs: DAY_END_RETRY_MS };
  }

  // 6. Vendor-side error with no body.
  if ((sig.httpStatus || 0) >= 500) {
    return { ...base, class: op === 'recordPayment' ? 'AMBIGUOUS' : 'RETRYABLE' };
  }

  // 7. Application-level failures: now, and only now, read the message.
  const cls = classifyMessage(message, op);
  if (cls) return { ...base, class: cls };

  // 8. Fail closed.
  return { ...base, class: 'VALIDATION' };
}

function classifyMessage(message: string, op: PmsOp): PmsError['class'] | null {
  // Vendor state disagrees with ours.
  if (/not vacant/i.test(message)) return 'CONFLICT';
  if (/already moved out/i.test(message)) return 'CONFLICT';

  // Business refusals: a person has to act, retrying never helps.
  if (/delinquent/i.test(message)) return 'BUSINESS_BLOCK';
  if (/sleepingthread|prepay limit|iteration count/i.test(message)) return 'BUSINESS_BLOCK';
  if (/do not rent/i.test(message)) return 'BUSINESS_BLOCK';
  if (/insurance is mandatory/i.test(message)) return 'BUSINESS_BLOCK';

  // Our stored external id is stale.
  if (/does not exist/i.test(message)) return 'NOT_FOUND';

  // SSM took money and failed to record it, or similar half-applied state.
  if (/refund is initiated/i.test(message)) return 'PARTIAL';

  // "Payment Failed" is retryable in general, but for a payment post we cannot
  // tell "nothing was recorded" from "recorded, then errored" — so a human
  // looks rather than risking a second receipt.
  if (/payment failed/i.test(message)) {
    return op === 'recordPayment' ? 'AMBIGUOUS' : 'RETRYABLE';
  }

  return null;
}

/**
 * SSM can report failure inside an HTTP 200, and @ssm/client only fails a call
 * when the embedded `Error` object is populated — so `{Success: false,
 * Message: "Payment Failed"}` arrives looking like a success. Every write must
 * therefore check the envelope itself.
 *
 * Note AddUpdateAccount has no `Success` field at all; a generated TenantId is
 * the only proof it worked.
 *
 * Returns null when the response really did succeed.
 */
export function checkSsmSuccessEnvelope(op: PmsOp, body: unknown): PmsError | null {
  if (body === null || typeof body !== 'object') {
    return {
      kind: 'ours',
      class: op === 'recordPayment' ? 'AMBIGUOUS' : 'VALIDATION',
      message: 'SSM returned a non-object response body.',
      raw: body,
    };
  }

  const b = body as Record<string, unknown>;
  const message = typeof b.Message === 'string' ? b.Message : '';

  if (op === 'ensureTenant') {
    const tenantId = b.TenantId;
    if (typeof tenantId === 'number' && tenantId > 0) return null;
    return classifySsmFailure(
      { kind: 'application', message: message || 'AddUpdateAccount returned no TenantId.', raw: body },
      op,
    );
  }

  if (op === 'moveIn' || op === 'recordPayment') {
    if (b.Success !== true) {
      return classifySsmFailure(
        { kind: 'application', message: message || 'SSM reported Success=false.', raw: body },
        op,
      );
    }
    const requiredField = op === 'moveIn' ? 'LeaseNumber' : 'ReceiptNumber';
    const value = b[requiredField];
    if (typeof value === 'number' && value > 0) return null;
    return {
      kind: 'application',
      class: 'AMBIGUOUS',
      message: `SSM reported success but returned no ${requiredField}.`,
      raw: body,
    };
  }

  return null;
}
